/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from 'react'
import { useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import {
  getActionLoading,
  getEditableSurvey,
  getSurveySections,
  getSurveyTitle
} from '../../store/slices/SurveyTemplateSlice/selectors'
import { type ISurveySection } from '../../store/slices/SurveyTemplateSlice'
import { notification } from '../../components/Notifications'
import { findInvalidQuestion, findInvalidSection, scrollToElement } from '../../features/SurveyTempleteFeature/helpers'
import _ from 'lodash'
import { type ISurveySection as ITemplateSurveySection, type ISurveyItem } from '../../models/SurveyTemplate'
import { StyledSaveMenuWrapper } from '../../features/SurveyTempleteFeature/SurveyHeader/styles'
import { Messages, type Question, SurveyItemQuestionType } from '../../features/SurveyTempleteFeature/types'
import { useMutation } from '@tanstack/react-query'
import { ADDED_QUESTION_TYPES } from '../../consts/addedQuestionTypes'
import { AutoImportManager } from '../../APIManager'

const SurveyHeader: React.FC = (): JSX.Element => {
  const theme = useTheme()
  const navigate = useNavigate()
  const surveyTitle = getSurveyTitle()
  const surveySections = getSurveySections()
  const editableSurvey = getEditableSurvey()

  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const slotId = params.get('slotId') ?? ''
  const divisionId = params.get('divisionId') ?? ''
  const type = params.get('type')
  const categoryName = params.get('categoryName') ?? ''
  const termId = params.get('termId') ?? ''
  const isLoading = getActionLoading()
  const checkIsTitleDirty = (): boolean => {
    if (surveyTitle !== editableSurvey.title) return true
    return false
  }

  const checkIsSectionDirty = (section: ISurveySection, sectionIndex: number): boolean => {
    const areAllSectionEqual = _.isEqual(editableSurvey?.surveySections?.[sectionIndex], section)
    return !areAllSectionEqual
  }

  const checkIsSectionIsOld = (section: ISurveySection): boolean => {
    const t = editableSurvey.surveySections.find((s: ITemplateSurveySection) => s.surveySectionId === section.surveySectionId)
    if (t?.surveySectionId) {
      return true
    }
    return false
  }

  const checkQuestionIsDirty = (sectionIndex: number, newQuestion: Question): boolean => {
    let isDirty = false
    editableSurvey?.surveySections?.[sectionIndex]?.surveyItems.forEach((question: ISurveyItem) => {
      if (question.itemId === newQuestion.itemId) {
        const isQuestionEqual = _.isEqual(question, newQuestion)
        isDirty = !isQuestionEqual
      }
    })
    return isDirty
  }

  const { mutate: saveQuestion, isPending } = useMutation({
    mutationFn: async (payload: any) => {
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
        return editableSurvey
          ? await AutoImportManager.patchAllInstructorQuestions(slotId, divisionId, payload)
          : await AutoImportManager.postAllInstructorQuestions(slotId, divisionId, payload)
      } else {
        return editableSurvey
          ? await AutoImportManager.patchAllAdminQuestions(slotId, divisionId, payload)
          : await AutoImportManager.postAllAdminQuestions(slotId, divisionId, payload)
      }
    },
    onSuccess: (response) => {
      const message = response?.data?.name ? `${response.data.name} added successfully` : 'Questions added successfully'
      notification(message, { variant: 'success' })
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
        navigate(`${ROUTES.INSTRUCTOR_SURVEYS}?termId=${termId ?? ''}`)
      } else {
        navigate(`${ROUTES.ADMIN_VIEWER_DASHBOARD}?termId=${termId ?? ''}`)
      }
    },
    onError: (err: any) => {
      const subTitle = err?.message.schema?.[0] || err?.message?.prompt || err?.message?.name || err?.message?.error || err?.error || 'An error occurred'
      notification(
        `Failed to ${editableSurvey ? 'update' : 'add'} questions`,
        { variant: 'error', subTitle }
      )
    }
  })

  const submitEditableSurvey = async (): Promise<void> => {
    const isTitleDirty = checkIsTitleDirty()
    const areAllSectionsEqual = _.isEqual(surveySections, editableSurvey?.surveySections)
    const isTemplateDirty = isTitleDirty || !areAllSectionsEqual

    let preparedTitle = ''
    if (!type) {
      preparedTitle = `${categoryName} added Questions`
    } else if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
      preparedTitle = 'Instructor-added Questions'
    }

    const preparedData = {
      title: surveyTitle,
      isDirty: isTemplateDirty,
      surveySections: surveySections?.map((section: ISurveySection, sectionIndex: number) => ({
        isDirty: checkIsSectionIsOld(section) ? checkIsSectionDirty(section, sectionIndex) : undefined,
        name: preparedTitle,
        isRepeatPerInstructor: section.isRepeatPerInstructor,
        title: section.title ?? undefined,
        descr: section.descr,
        surveySectionId: section.surveySectionId ?? undefined,
        attributes: section.attributes,
        branchingFilters: section.branchingFilters,
        indentation: section.indentation,
        parentSurveySectionId: section.parentSurveySectionId,
        revision: section.revision,
        surveyItems: section.surveyItems?.map((question: Question) => ({
          isDirty: question.itemId && checkQuestionIsDirty(sectionIndex, question),
          itemId: question.itemId ?? undefined,
          itemType: question.itemType,
          prompt: question.prompt,
          isRequired: question.isRequired,
          revision: question.revision,
          branchingFilters: question?.branchingFilters,
          ...(question.itemType === SurveyItemQuestionType.MULTIPLE_CHOICE ||
            question.itemType === SurveyItemQuestionType.LINEAR_SCALE ||
            question.itemType === SurveyItemQuestionType.MULTI_SELECT
            ? { object: question.object }
            : {})
        }))
      }))
    }
    saveQuestion(preparedData.surveySections[0])
  }

  const submitCreateSurvey = async (): Promise<any> => {
    let preparedName = surveyTitle
    if (!type) {
      preparedName = `${categoryName} added Questions`
    } else if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
      preparedName = 'Instructor-added Questions'
    }
    const preparedSection = {
      ...surveySections[0],
      name: preparedName
    }
    saveQuestion(preparedSection)
  }

  const handleSave = async (): Promise<void> => {
    const submitSurvey = editableSurvey ? submitEditableSurvey : submitCreateSurvey
    await submitSurvey()
  }

  useEffect(() => {
    findInvalidSection(surveySections)
  }, [surveySections])

  return (
    <React.Fragment>
      <StyledSaveMenuWrapper>
        <GenericButton
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Medium}
          loading={isLoading || isPending}
          label={editableSurvey ? 'Save' : 'Add'}
          onClick={() => {
            void handleSave()
          }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
          disabled={false}
        />
      </StyledSaveMenuWrapper>

    </React.Fragment>
  )
}
export default SurveyHeader
